import { DELIVERY_LOCATIONS, ALLOWED_PINCODES } from "../data/deliveryLocations";

export const SHIPPING_CHARGE = 100;
export const FREE_SHIPPING_THRESHOLD = 1000;
export const STATE_NAME = "Tamil Nadu";
export const COUNTRY_NAME = "India";
export const DELIVERY_STORAGE_KEY = "selectedDeliveryLocation";

export const createEmptyAddress = () => ({
  country: COUNTRY_NAME,
  firstName: "",
  lastName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  district: "",
  state: STATE_NAME,
  pincode: "",
  phone: "",
  landmark: "",
  latitude: "",
  longitude: "",
  formattedAddress: "",
  region: "",
  locationId: null,
  deliveryAvailable: true,
});

export const normalizeText = (value = "") =>
  String(value).trim().toLowerCase().replace(/\s+/g, " ");

export const findMatchingLocation = ({ area = "", city = "", district = "", pincode = "", formattedAddress = "" }) => {
  const nArea = normalizeText(area);
  const nCity = normalizeText(city);
  const nDistrict = normalizeText(district);
  const nFormatted = normalizeText(formattedAddress);

  return DELIVERY_LOCATIONS.find((item) => {
    const itemArea = normalizeText(item.area);
    const itemCity = normalizeText(item.city);
    const itemDistrict = normalizeText(item.district);
    const itemRegion = normalizeText(item.region);

    const samePincode = item.pincode === String(pincode || "").trim();

    const areaMatched =
      nArea.includes(itemArea) ||
      itemArea.includes(nArea) ||
      nFormatted.includes(itemArea);

    const cityMatched =
      nCity === itemCity ||
      nDistrict === itemDistrict ||
      nFormatted.includes(itemCity) ||
      nFormatted.includes(itemDistrict) ||
      nFormatted.includes(itemRegion);

    return samePincode && (areaMatched || cityMatched);
  });
};

/**
 * Validates delivery availability and calculates accurate shipping charges
 * Supports Amazon/Flipkart-style seamless pincode delivery calculation.
 */
export const checkDeliveryAvailabilityLocal = (addressPayload = {}, subtotal = 0) => {
  const pincode = String(addressPayload.pincode || "").trim();
  const city = String(addressPayload.city || "").trim();
  const state = String(addressPayload.state || STATE_NAME).trim();
  const addressLine1 = String(addressPayload.addressLine1 || "").trim();

  // Validate 6-digit Indian PIN code format
  if (!/^\d{6}$/.test(pincode)) {
    return {
      serviceable: false,
      available: false,
      shippingCharge: 0,
      message: "Please enter a valid 6-digit PIN code.",
      matchedLocation: null,
    };
  }

  // Calculate free delivery vs standard shipping
  const isFreeDelivery = Number(subtotal) >= FREE_SHIPPING_THRESHOLD;
  const applicableCharge = isFreeDelivery ? 0 : SHIPPING_CHARGE;

  const isLocalDirect = ALLOWED_PINCODES.includes(pincode);
  const matchedLocation = findMatchingLocation({
    area: addressLine1,
    city,
    pincode,
    formattedAddress: addressPayload.formattedAddress,
  });

  const cityName = city || matchedLocation?.city || "your location";

  let message = "";
  if (isFreeDelivery) {
    message = `Free Delivery applied to ${cityName} (${pincode})!`;
  } else {
    message = `Standard Delivery to ${cityName} (${pincode}) - Rs. ${applicableCharge}. (Free on orders above Rs. ${FREE_SHIPPING_THRESHOLD})`;
  }

  return {
    serviceable: true,
    available: true,
    isLocalDirect,
    shippingCharge: applicableCharge,
    freeDeliveryEligible: isFreeDelivery,
    message,
    matchedLocation: matchedLocation || {
      area: addressLine1 || cityName,
      city: cityName,
      district: addressPayload.district || cityName,
      state: state || STATE_NAME,
      pincode,
    },
  };
};

export const createAddressFromLocation = (location) => ({
  country: COUNTRY_NAME,
  firstName: "",
  lastName: "",
  addressLine1: location.area,
  addressLine2: "",
  city: location.city,
  district: location.district,
  state: location.state || STATE_NAME,
  pincode: location.pincode,
  phone: "",
  landmark: "",
  latitude: "",
  longitude: "",
  formattedAddress: `${location.area}, ${location.city}, ${location.district}, ${location.state || STATE_NAME} - ${location.pincode}`,
  region: location.region,
  locationId: location.id,
  deliveryAvailable: true,
});

export const saveSelectedDeliveryLocation = (payload) => {
  localStorage.setItem(DELIVERY_STORAGE_KEY, JSON.stringify(payload));
};

export const getSelectedDeliveryLocation = () => {
  const raw = localStorage.getItem(DELIVERY_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearSelectedDeliveryLocation = () => {
  localStorage.removeItem(DELIVERY_STORAGE_KEY);
};